import {
  HttpErrorResponse,
  HttpEventType,
  HttpInterceptorFn,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';

import { AuthService } from '@auth/services/http/auth/auth';
import { apiHideErrorToastContext } from '@platform/shared/services/utils/toast-error.context';
import { apiHideSuccessToastContext } from '@platform/shared/services/utils/toast-success.context';
import {
  extractApiErrorCode,
  extractApiErrorMessage,
} from '@core/api/api-extract-error';
import { ApiResponse } from '@core/api/api-response';
import { ToastService } from '@platform/shared/services/utils/toast.service';

const logoutErrorCodes = new Set(['UNAUTHENTICATED', 'INVALID_ACCESS_TOKEN']);

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const auth = inject(AuthService);

  return next(req).pipe(
    tap((event) => {
      if (event.type !== HttpEventType.Response) {
        return;
      }

      if (req.context.get(apiHideSuccessToastContext)) {
        return;
      }

      const response = event as HttpResponse<ApiResponse<unknown>>;
      const message = response.body?.message;
      if (typeof message === 'string' && message.trim().length > 0) {
        toast.success(message);
      }
    }),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const message = extractApiErrorMessage(err.error);
        const errorCode = extractApiErrorCode(err.error);
        const hideErrorToast = req.context.get(apiHideErrorToastContext);

        if (!hideErrorToast) {
          if (err.status === HttpStatusCode.InternalServerError) {
            toast.error(
              message && message.trim().length > 0
                ? message
                : 'Algo deu errado. Tente novamente mais tarde.',
            );
          } else if (message) {
            toast.error(message);
          } else if (err.status === 0) {
            toast.error(
              'Parece que você está offline. Verifique sua conexão e tente novamente.',
            );
          }
        }

        if (
          err.status === HttpStatusCode.Unauthorized &&
          logoutErrorCodes.has(errorCode ?? '')
        ) {
          auth.logout();
        }

        return throwError(() => err);
      }
      return throwError(() => err);
    }),
  );
};
